import { useLazyQuery } from '@apollo/client';
import { CHECK_DUPLICATE_PAYMENT_REQUEST_ITEMS, CHECK_MY_DUPLICATE_PAYMENT_REQUEST_ITEMS, } from '@src/graphql/queries/paymentRequest';
import { getActiveItems } from '@src/utilities/paymentRequestHelper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/**
 * Hook for members to check their own duplicate payment request items.
 * Automatically uses the current user's context from the backend.
 *
 * @param paymentRequest - The payment request to check for duplicates
 * @param program - The program associated with the payment request
 * @returns Object containing duplicate information, loading state, and control functions
 *
 * @example
 * ```tsx
 * const {
 *   duplicateInfo,
 *   duplicatesChecked,
 *   loading,
 *   error,
 *   checkForDuplicates,
 *   resetDuplicateCheck
 * } = useCheckMyPaymentRequestDuplicates({ paymentRequest, program });
 *
 * // Trigger duplicate check when entering review step
 * useEffect(() => {
 *   if (currentStep === 'review') {
 *     checkForDuplicates();
 *   }
 * }, [currentStep, checkForDuplicates]);
 * ```
 */
export const useCheckMyPaymentRequestDuplicates = ({ paymentRequest, program, }) => {
    const [duplicateInfo, setDuplicateInfo] = useState([]);
    const [duplicatesChecked, setDuplicatesChecked] = useState(false);
    const checkRequestedRef = useRef(false);
    const isMountedRef = useRef(true);
    const [checkDuplicates, { data: duplicateData, loading, error }] = useLazyQuery(CHECK_MY_DUPLICATE_PAYMENT_REQUEST_ITEMS);
    // Memoize activeItems to prevent unnecessary recalculations
    const activeItems = useMemo(() => getActiveItems(paymentRequest), [paymentRequest]);
    // Track component mount status to prevent state updates after unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    // Handle duplicate check response
    useEffect(() => {
        if (duplicateData?.checkMyDuplicatePaymentRequestItems) {
            // Only update state if component is still mounted
            if (!isMountedRef.current)
                return;
            const duplicates = duplicateData.checkMyDuplicatePaymentRequestItems;
            if (duplicates.length > 0) {
                // Flatten duplicates to match existing duplicateInfo structure
                const flatDuplicates = duplicates.flatMap((item) => item.duplicates.map(dup => ({
                    tempId: item.tempId,
                    paymentRequestId: dup.paymentRequestId,
                    expenseType: dup.paymentRequestItem?.type || '',
                })));
                setDuplicateInfo(flatDuplicates);
            }
            else {
                setDuplicateInfo([]);
            }
            setDuplicatesChecked(true);
            checkRequestedRef.current = false;
        }
    }, [duplicateData]);
    const performDuplicateCheck = useCallback(async () => {
        if (activeItems.length === 0 || checkRequestedRef.current) {
            return;
        }
        // Mark that we've requested a check to prevent duplicate requests
        checkRequestedRef.current = true;
        // Clear previous duplicate info before new check
        setDuplicateInfo([]);
        // Prepare items for duplicate checking using the new spec structure
        const itemsToCheck = activeItems.map((item) => ({
            tempId: item.tempId,
            // Handle empty string for expenseType - convert to undefined
            expenseType: item.type && item.type.trim() !== '' ? item.type : undefined,
            expenseTitle: item.title,
        }));
        try {
            await checkDuplicates({
                variables: {
                    spec: {
                        // No clientId or userId needed - automatically uses current user from context
                        termStartDate: paymentRequest.termStartDate,
                        termEndDate: paymentRequest.termEndDate,
                        programName: program.programName,
                        programType: program.programLevelName,
                        schoolName: program.otherSchool || program.schoolName,
                        submittedAt: paymentRequest.submittedAt,
                        excludePaymentRequestNum: paymentRequest.requestNum, // Exclude current claim from duplicate check (important for re-submissions)
                        items: itemsToCheck,
                    },
                },
            });
        }
        catch (err) {
            // If query fails, reset the request flag so user can retry
            checkRequestedRef.current = false;
            console.error('Failed to check for duplicates:', err);
        }
    }, [activeItems, paymentRequest, program, checkDuplicates]);
    /**
     * Manually trigger duplicate check.
     * Component should call this when entering the review step.
     * Only performs check if not already checked and there are active items.
     */
    const checkForDuplicates = useCallback(() => {
        if (!duplicatesChecked && activeItems.length > 0) {
            performDuplicateCheck();
        }
    }, [duplicatesChecked, activeItems.length, performDuplicateCheck]);
    /**
     * Reset duplicate check state.
     * Call this when user makes changes to payment request items
     * or when navigating away from the review step.
     *
     * Note: If called while a query is in flight, the response will be ignored
     * due to the mounted ref check, preventing memory leaks.
     */
    const resetDuplicateCheck = useCallback(() => {
        if (!isMountedRef.current)
            return;
        setDuplicatesChecked(false);
        setDuplicateInfo([]);
        checkRequestedRef.current = false;
    }, []);
    return {
        duplicateInfo,
        duplicatesChecked,
        loading,
        error,
        checkForDuplicates,
        resetDuplicateCheck,
    };
};
/**
 * Hook for admins to check duplicate payment request items for any user.
 * Requires clientId and userId to be provided.
 *
 * @param paymentRequest - The payment request to check for duplicates (with clientId and userId)
 * @param program - The program associated with the payment request (optional if using paymentRequest.program)
 * @returns Object containing duplicate information, loading state, and control functions
 *
 * @example
 * ```tsx
 * const {
 *   duplicates,
 *   loading,
 *   error,
 *   checkForDuplicates
 * } = useCheckPaymentRequestDuplicates({
 *   paymentRequest,
 *   program: paymentRequest.program
 * });
 *
 * // Automatically check when payment request loads
 * useEffect(() => {
 *   if (paymentRequest) {
 *     checkForDuplicates();
 *   }
 * }, [paymentRequest, checkForDuplicates]);
 * ```
 */
export const useCheckPaymentRequestDuplicates = ({ paymentRequest, program, }) => {
    const [duplicates, setDuplicates] = useState([]);
    const checkRequestedRef = useRef(false);
    const isMountedRef = useRef(true);
    // Use ref to always access the latest paymentRequest to avoid stale closure
    const paymentRequestRef = useRef(paymentRequest);
    const programRef = useRef(program);
    const [checkDuplicates, { data: duplicateData, loading, error }] = useLazyQuery(CHECK_DUPLICATE_PAYMENT_REQUEST_ITEMS);
    // Update refs when props change
    useEffect(() => {
        paymentRequestRef.current = paymentRequest;
        programRef.current = program;
    }, [paymentRequest, program]);
    // Track component mount status to prevent state updates after unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    // Handle duplicate check response
    useEffect(() => {
        if (duplicateData?.checkDuplicatePaymentRequestItems) {
            // Only update state if component is still mounted
            if (!isMountedRef.current)
                return;
            setDuplicates(duplicateData.checkDuplicatePaymentRequestItems);
            checkRequestedRef.current = false;
        }
    }, [duplicateData]);
    // Reset the check flag when payment request ID changes (e.g., navigating to a different claim)
    // This allows the duplicate check to run again on the new payment request
    // Don't clear duplicates here - let them persist until new data arrives to avoid UI flicker
    useEffect(() => {
        checkRequestedRef.current = false;
    }, [paymentRequest?.id]);
    /**
     * Trigger duplicate check for the current payment request.
     * Requires paymentRequest to have clientId, userId (from program.employee.userId),
     * and program information.
     *
     * Note: This callback uses refs to access the latest paymentRequest and program data,
     * avoiding stale closure issues. The dependency array only includes the ID to prevent
     * unnecessary re-triggering of effects that depend on this function.
     */
    const checkForDuplicates = useCallback(async () => {
        // Use refs to get the latest values, avoiding stale closure
        const currentPaymentRequest = paymentRequestRef.current;
        const currentProgram = programRef.current;
        if (!currentPaymentRequest || checkRequestedRef.current) {
            return;
        }
        // Determine program to use (prop or from paymentRequest)
        const programToUse = currentProgram || currentPaymentRequest.program;
        if (!programToUse) {
            console.error('Program is required for duplicate checking');
            return;
        }
        if (!programToUse.employee?.userId) {
            console.error('Program employee userId is required for duplicate checking');
            return;
        }
        if (currentPaymentRequest.paymentRequestItems.length === 0) {
            return;
        }
        // Mark that we've requested a check to prevent duplicate requests
        checkRequestedRef.current = true;
        // Clear previous duplicates when starting a new check
        // This ensures stale data from previous payment requests doesn't persist
        setDuplicates([]);
        // Prepare items for duplicate checking
        const itemsToCheck = currentPaymentRequest.paymentRequestItems
            .filter(item => item.tempId) // Only include items with a valid tempId
            .map(item => ({
            tempId: item.tempId,
            // Handle empty string for expenseType - convert to undefined
            expenseType: item.type && item.type.trim() !== '' ? item.type : undefined,
            expenseTitle: item.title,
        }));
        try {
            await checkDuplicates({
                variables: {
                    spec: {
                        clientId: currentPaymentRequest.clientId,
                        userId: programToUse.employee.userId,
                        termStartDate: currentPaymentRequest.termStartDate,
                        termEndDate: currentPaymentRequest.termEndDate,
                        programName: programToUse.programName,
                        programType: programToUse.programLevelName,
                        schoolName: programToUse.otherSchool || programToUse.schoolName,
                        excludePaymentRequestNum: currentPaymentRequest.requestNum,
                        submittedAt: currentPaymentRequest.submittedAt,
                        items: itemsToCheck,
                    },
                },
            });
        }
        catch (err) {
            // If query fails, reset the request flag so it can be retried
            checkRequestedRef.current = false;
            console.error('Failed to check for duplicates:', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentRequest?.id, checkDuplicates]); // Only recreate when ID or query function changes
    return {
        duplicates,
        loading,
        error,
        checkForDuplicates,
    };
};
